import { doc, updateDoc, arrayUnion, arrayRemove, getDoc } from "firebase/firestore";
import { createTimestamp } from "@/utils/dateUtils";
import { appointmentsCol, getCurrentUser } from "./utils";
import type { LawyerNote, ActionItem } from "./types";

/**
 * Add a lawyer note to an appointment
 */
export const addLawyerNote = async (
  appointmentId: string,
  content: string,
  type: "general" | "session" | "follow-up" | "summary" = "general"
): Promise<void> => {
  const user = getCurrentUser();
  
  const noteData: LawyerNote = {
    id: `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    content: content.trim(),
    type,
    createdAt: new Date().toISOString(),
  };

  const docRef = doc(appointmentsCol(user.uid), appointmentId);
  
  await updateDoc(docRef, {
    notes: arrayUnion(noteData),
    updatedAt: createTimestamp(),
  });
};

/**
 * Delete a lawyer note from an appointment
 */
export const deleteLawyerNote = async (
  appointmentId: string,
  noteId: string
): Promise<void> => {
  const user = getCurrentUser();
  
  // First, get the current appointment to find the note
  const docRef = doc(appointmentsCol(user.uid), appointmentId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    throw new Error("Appointment not found");
  }
  
  const appointmentData = docSnap.data();
  const notes = appointmentData.notes || [];
  const noteToDelete = notes.find((note: LawyerNote) => note.id === noteId);
  
  if (!noteToDelete) {
    throw new Error("Note not found");
  }
  
  await updateDoc(docRef, {
    notes: arrayRemove(noteToDelete),
    updatedAt: createTimestamp(),
  });
};

/**
 * Add an action item to an appointment
 */
export const addActionItem = async (
  appointmentId: string,
  title: string,
  description: string,
  dueDate: string,
  priority: "low" | "medium" | "high" = "medium"
): Promise<void> => {
  const user = getCurrentUser();
  
  const actionData: ActionItem = {
    id: `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    title: title.trim(),
    description: description.trim(),
    dueDate,
    priority,
    status: "pending",
    createdAt: new Date().toISOString(),
  };

  const docRef = doc(appointmentsCol(user.uid), appointmentId);
  
  await updateDoc(docRef, {
    actions: arrayUnion(actionData),
    updatedAt: createTimestamp(),
  });
};

/**
 * Update the status of an action item
 */
export const updateActionItemStatus = async (
  appointmentId: string,
  actionId: string,
  status: "pending" | "in_progress" | "completed"
): Promise<void> => {
  const user = getCurrentUser();
  
  // Get the current appointment data
  const docRef = doc(appointmentsCol(user.uid), appointmentId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    throw new Error("Appointment not found");
  }
  
  const appointmentData = docSnap.data();
  const actions = appointmentData.actions || [];
  const actionIndex = actions.findIndex((action: ActionItem) => action.id === actionId);
  
  if (actionIndex === -1) {
    throw new Error("Action item not found");
  }
  
  // Update the action item
  const updatedAction = {
    ...actions[actionIndex],
    status,
  };
  
  // Replace the action in the array
  const updatedActions = [...actions];
  updatedActions[actionIndex] = updatedAction;
  
  await updateDoc(docRef, {
    actions: updatedActions,
    updatedAt: createTimestamp(),
  });
};

/**
 * Delete an action item from an appointment
 */
export const deleteActionItem = async (
  appointmentId: string,
  actionId: string
): Promise<void> => {
  const user = getCurrentUser();
  
  // First, get the current appointment to find the action
  const docRef = doc(appointmentsCol(user.uid), appointmentId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    throw new Error("Appointment not found");
  }
  
  const appointmentData = docSnap.data();
  const actions = appointmentData.actions || [];
  const actionToDelete = actions.find((action: ActionItem) => action.id === actionId);
  
  if (!actionToDelete) {
    throw new Error("Action item not found");
  }
  
  await updateDoc(docRef, {
    actions: arrayRemove(actionToDelete),
    updatedAt: createTimestamp(),
  });
};
